import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { getBillingOptions, getIncentivePlanOptions, getTenant, setRequestedTenantId } from './Tenants.slice'
import { updateTenantApi, getTenantAccountStatusApi } from '../../../apis/apis'
import { fetchAllTenantRegistrationRecords } from '../../../apis/registration'
import PanelContact from './components/PanelContact'
import HeaderPrograms from './components/HeaderPrograms'
import TabMailingPreference from './components/TabMailingPreference'
import InlineEditInput from '../../../components/Form/InlineEditInput'
import TabPanel from './components/TabPanel'
import TabGeneral from './components/TabGeneral'
import TabServiceAgreements from './components/TabServiceAgreements'
import TabAccountManagers from './components/TabAccountManagers'
import TabPayments from './components/TabPayments'
import TabInvoices from './components/TabInvoices'
import TabDocuments from './components/TabDocuments'
import TabBillingEvents from './components/TabBillingEvents'
import classnames from 'classnames'
import TabBillingOptions from './components/TabBillingOptions'
import TabIncentivePlans from './components/TabIncentivePlans'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import * as Yup from 'yup'
import { conductorTabIndex, setTenantViewFocus, tabTitleForIndex } from '../../chat/tenantViewFocus'
import { setNotesViewContext } from '../../notes/notesViewContext'
import TabConductor from './components/TabConductor'
import ConductorAccountChip from './components/ConductorAccountChip'
import { TenantAccountBillingStatusResponse, TenantAccountStatusResponse, ServiceAccount } from '../../../apis/types'
import TenantAccountPanel from './components/TenantAccountPanel'
import TenantAccountsToolbar from './components/TenantAccountsToolbar'
import TenantKpiBar from './components/TenantKpiBar'
import { computeTenantKpis, getInitialLoadTabBadges } from './components/accountStatusUtils'
import {
  DEFAULT_HEADER_CHIP_IDS,
  type HeaderChipId,
  readHeaderChipIds,
  toggleHeaderChip,
  writeHeaderChipIds,
} from './components/headerChipPrefs'
import { parseTenantDeepLink, retireTenantDeepLinkSearchParams, shouldClearDeepLinkOnAccountOpen } from './tenantDeepLink'
import { useDeepLinkAccountScroll } from './useDeepLinkAccountScroll'

declare global {
  interface Window {
    impersonation_ephemeralToken: string | undefined
    impersonation_email: string | undefined
  }
}

const AUTO_SCROLL_STORAGE_KEY = 'tenantDetail.autoScroll'
const LAST_TAB_STORAGE_KEY = 'tenantDetail.lastTab'
const HEADER_META_LINK =
  'font-medium text-slate-800 underline transition-colors hover:text-indigo-700 dark:text-slate-100 dark:hover:text-indigo-300'

const matchesServiceAccountNumber = (account: ServiceAccount, query: string): boolean =>
  account.kingAccountNumber === query || account.utilityAccountNumber === query

const readSessionBool = (key: string, fallback: boolean): boolean => {
  try {
    const stored = sessionStorage.getItem(key)
    if (stored === 'true') return true
    if (stored === 'false') return false
  } catch {
    // ignore unavailable sessionStorage
  }
  return fallback
}

const writeSessionBool = (key: string, value: boolean): void => {
  try {
    sessionStorage.setItem(key, value ? 'true' : 'false')
  } catch {
    // ignore unavailable sessionStorage
  }
}

const readSessionInt = (key: string, fallback: number): number => {
  try {
    const stored = sessionStorage.getItem(key)
    if (stored == null) return fallback
    const parsed = Number.parseInt(stored, 10)
    if (Number.isFinite(parsed) && parsed >= 0) return parsed
  } catch {
    // ignore unavailable sessionStorage
  }
  return fallback
}

const writeSessionInt = (key: string, value: number): void => {
  try {
    sessionStorage.setItem(key, String(value))
  } catch {
    // ignore unavailable sessionStorage
  }
}

const Tenant: React.FC = () => {
  const [isInitializing, setInitializing] = useState(true)
  const [isHighlighting, setHighlighting] = useState(false)
  const [billingStatuses, setBillingStatuses] = useState<{ [key: string]: TenantAccountBillingStatusResponse }>({})
  const [accountStatuses, setAccountStatuses] = useState<{ [key: string]: TenantAccountStatusResponse }>({})
  const [statusesLoaded, setStatusesLoaded] = useState(false)
  const [registrationId, setRegistrationId] = useState<string | undefined>(undefined)
  const [openByAccountId, setOpenByAccountId] = useState<Record<string, boolean>>({})
  const [expandAllOpen, setExpandAllOpen] = useState(true)
  const [expandAllVersion, setExpandAllVersion] = useState(0)
  const [autoScroll, setAutoScrollState] = useState(() => readSessionBool(AUTO_SCROLL_STORAGE_KEY, true))
  const [pinnedChipIds, setPinnedChipIdsState] = useState<HeaderChipId[]>(() => readHeaderChipIds())
  const [lastTabIndex, setLastTabIndexState] = useState(() => readSessionInt(LAST_TAB_STORAGE_KEY, 0))
  const [tabByAccountId, setTabByAccountId] = useState<Record<string, number>>({})
  const [conductorTabBadges, setConductorTabBadges] = useState<Record<string, number | string | undefined>>({})
  const lastTabIndexRef = useRef(lastTabIndex)
  const accountRefs = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({})
  const pageHeaderRef = useRef<HTMLDivElement>(null)
  const accountsToolbarRef = useRef<HTMLDivElement>(null)
  const [pageHeaderHeight, setPageHeaderHeight] = useState(0)
  const [accountsToolbarHeight, setAccountsToolbarHeight] = useState(0)
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const [, setSearchParams] = useSearchParams()
  const searchParams = new URLSearchParams(search)
  const { serviceAccountNumber: qpServiceAccountNumber, tab: qpTab } = parseTenantDeepLink(
    searchParams.get('serviceAccountNumber'),
    searchParams.get('tab')
  )
  const { tenantId } = useParams()
  const { permissions } = useAppSelector((state) => state.app)

  const { loading, refreshingTenant, error, tenant, billingOptions, incentivePlanOptions } = useAppSelector((state) => state.tenants)

  const setLastTabIndex = useCallback((index: number): void => {
    lastTabIndexRef.current = index
    setLastTabIndexState(index)
    writeSessionInt(LAST_TAB_STORAGE_KEY, index)
  }, [])

  const setAutoScroll = useCallback((enabled: boolean): void => {
    setAutoScrollState(enabled)
    writeSessionBool(AUTO_SCROLL_STORAGE_KEY, enabled)
  }, [])

  const setPinnedChipIds = useCallback((ids: HeaderChipId[]): void => {
    setPinnedChipIdsState(ids)
    writeHeaderChipIds(ids)
  }, [])

  const hasSalesforceOpsROPermission = (): boolean => {
    if (permissions == null) return false
    return permissions?.includes('sfops:view:link')
  }
  const hasSalesforceOpsRWPermission = (): boolean => {
    if (permissions == null) return false
    return permissions?.includes('sfops:edit:id') && permissions?.includes('sfops:view:link')
  }
  const hasServiceAgreementSuperuserPermission = (): boolean => {
    if (permissions == null) return false
    return permissions.includes('account:manager:service-agreement:superuser')
  }
  const hasConductorRead = permissions?.includes('conductor:read') === true
  const conductorTab = conductorTabIndex(hasConductorRead)

  useEffect(() => {
    billingOptions == null && dispatch(getBillingOptions())
    incentivePlanOptions == null && dispatch(getIncentivePlanOptions())
    let active = true
    dispatch(setRequestedTenantId(tenantId as string))
    setInitializing(true)
    dispatch(getTenant(tenantId as string)).finally(() => {
      if (active) {
        setInitializing(false)
      }
    })
    return () => {
      active = false
    }
  }, [dispatch, tenantId])

  useEffect(() => {
    setBillingStatuses({})
    setAccountStatuses({})
    setStatusesLoaded(false)
  }, [tenantId])

  useEffect(() => {
    if (!tenantId) return
    fetchAllTenantRegistrationRecords({ tenantId, resultsPerPage: 1 }).then((result) => {
      setRegistrationId(result.results[0]?.id)
    })
  }, [tenantId])

  // Seed / sync open state when accounts or deep-link target change
  useEffect(() => {
    if (!tenant?.serviceAccounts?.length) return
    setOpenByAccountId((prev) => {
      const next: Record<string, boolean> = {}
      const hasDeepLink = Boolean(qpServiceAccountNumber)
      tenant.serviceAccounts.forEach((account, index) => {
        if (hasDeepLink && qpServiceAccountNumber != null) {
          next[account.id] = matchesServiceAccountNumber(account, qpServiceAccountNumber)
        } else if (prev[account.id] != null) {
          next[account.id] = prev[account.id]
        } else {
          next[account.id] = index === 0
        }
      })
      return next
    })
  }, [tenant?.serviceAccounts, qpServiceAccountNumber])

  const deepLinkSeedKeyRef = useRef<string | null>(null)

  // Seed tab selection from `?tab=` whenever the deep-link query changes.
  // Without this, stale tabByAccountId from an earlier visit on the same route wins over the URL.
  useEffect(() => {
    if (!tenant?.serviceAccounts?.length) return
    if (qpServiceAccountNumber == null && qpTab == null) return

    const accountIds = tenant.serviceAccounts.map((account) => account.id).join(',')
    const seedKey = `${qpServiceAccountNumber ?? ''}|${qpTab ?? ''}|${accountIds}`
    if (deepLinkSeedKeyRef.current === seedKey) return
    deepLinkSeedKeyRef.current = seedKey

    const tabIndex = qpTab ?? 0
    setLastTabIndex(tabIndex)
    setTabByAccountId((prev) => {
      let changed = false
      const next = { ...prev }
      const applyTab = (accountId: string): void => {
        if (next[accountId] !== tabIndex) {
          next[accountId] = tabIndex
          changed = true
        }
      }

      if (qpServiceAccountNumber != null) {
        for (const account of tenant.serviceAccounts) {
          if (matchesServiceAccountNumber(account, qpServiceAccountNumber)) applyTab(account.id)
        }
      }

      return changed ? next : prev
    })

    // Repair malformed deep links that dropped `&` before `tab=` (e.g. `…?serviceAccountNumber=1211130-1tab=4`).
    const urlParams = new URLSearchParams(search)
    const urlTab = urlParams.get('tab')
    const urlSan = urlParams.get('serviceAccountNumber')
    const needsSanRepair = qpServiceAccountNumber != null && urlSan != null && urlSan !== qpServiceAccountNumber
    const needsTabRepair = qpTab != null && urlTab !== String(qpTab)
    if (needsSanRepair || needsTabRepair) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (qpServiceAccountNumber != null) next.set('serviceAccountNumber', qpServiceAccountNumber)
          if (qpTab != null) next.set('tab', String(qpTab))
          return next
        },
        { replace: true }
      )
    }
  }, [tenant?.serviceAccounts, qpServiceAccountNumber, qpTab, setLastTabIndex, setSearchParams, search])

  const deepLinkAccount = useMemo(() => {
    if (qpServiceAccountNumber == null || !tenant?.serviceAccounts?.length) return undefined
    return tenant.serviceAccounts.find((account) => matchesServiceAccountNumber(account, qpServiceAccountNumber))
  }, [qpServiceAccountNumber, tenant?.serviceAccounts])

  const deepLinkKingAccountNumber = deepLinkAccount?.kingAccountNumber
  const deepLinkAccountId = deepLinkAccount?.id

  useLayoutEffect(() => {
    // Header node only exists after the loading gate; re-bind whenever that tree mounts.
    if ((loading && !refreshingTenant) || isInitializing) return

    const pageEl = pageHeaderRef.current
    if (pageEl == null) return

    const syncPage = (): void => {
      // `#header` uses sticky `-top-px`, so its painted bottom sits 1px above `height`.
      setPageHeaderHeight(Math.max(0, Math.ceil(pageEl.getBoundingClientRect().height) - 1))
    }
    syncPage()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(syncPage)
    observer.observe(pageEl)
    return () => observer.disconnect()
  }, [isInitializing, loading, refreshingTenant])

  useLayoutEffect(() => {
    if ((loading && !refreshingTenant) || isInitializing) return
    const toolbarEl = accountsToolbarRef.current
    if (toolbarEl == null) {
      setAccountsToolbarHeight(0)
      return
    }

    const syncToolbar = (): void => {
      setAccountsToolbarHeight(Math.max(0, Math.ceil(toolbarEl.getBoundingClientRect().height)))
    }
    syncToolbar()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(syncToolbar)
    observer.observe(toolbarEl)
    return () => observer.disconnect()
  }, [isInitializing, loading, refreshingTenant, tenant?.serviceAccounts?.length])

  // Overlap 1px so ceil() rounding and the page header's `-top-px` cannot leave a
  // hairline gap where scrolled account content shows through under the toolbar.
  const accountStickyOffset = Math.max(0, pageHeaderHeight + accountsToolbarHeight - 1)

  // Highlight deep-linked account as soon as tenant data is ready (no 1s delay)
  useEffect(() => {
    if (loading || !qpServiceAccountNumber || !tenant?.serviceAccounts) {
      if (!qpServiceAccountNumber) setHighlighting(false)
      return
    }
    setHighlighting(deepLinkAccount != null)
  }, [loading, qpServiceAccountNumber, tenant?.serviceAccounts, deepLinkAccount])

  // Ensure refs exist during render so scroll/highlight can attach immediately
  if (tenant?.serviceAccounts) {
    tenant.serviceAccounts.forEach((account) => {
      if (accountRefs.current[account.kingAccountNumber] == null) {
        accountRefs.current[account.kingAccountNumber] = React.createRef<HTMLDivElement>()
      }
    })
  }

  const fetchAccountStatuses = async (isActive: () => boolean) => {
    if (tenant?.serviceAccounts) {
      const statusMap: { [key: string]: TenantAccountStatusResponse } = {}
      const needsFetch: typeof tenant.serviceAccounts = []

      for (const account of tenant.serviceAccounts) {
        if (account.accountStatus != null) {
          const seeded: TenantAccountStatusResponse = {
            status: account.accountStatus,
            cancellationReason: account.cancellationReason ?? null,
            cancellationNoticeDays: account.cancellationNoticeDays ?? null,
            pendingCloseStartedAt: account.pendingCloseStartedAt ?? null,
          }
          // PENDING_CLOSED needs notice fields for panel subtext; refetch if GET /tenant omitted them.
          if (
            account.accountStatus === 'PENDING_CLOSED' &&
            (seeded.cancellationNoticeDays == null || seeded.pendingCloseStartedAt == null)
          ) {
            needsFetch.push(account)
          } else {
            statusMap[account.id] = seeded
          }
        } else {
          needsFetch.push(account)
        }
      }

      if (needsFetch.length > 0) {
        const statusPromises = needsFetch.map(async (account) => {
          try {
            const status = await getTenantAccountStatusApi(account.id)
            return { accountId: account.id, status }
          } catch (error) {
            console.error(`Failed to fetch account status for account ${account.id}:`, error)
            return { accountId: account.id, status: null }
          }
        })

        const results = await Promise.all(statusPromises)
        if (!isActive()) {
          return false
        }
        for (const { accountId, status } of results) {
          if (status) statusMap[accountId] = status
        }
      }

      if (!isActive()) {
        return false
      }
      setAccountStatuses(statusMap)
      return true
    }
    return false
  }

  const fetchBillingStatuses = async (isActive: () => boolean) => {
    if (tenant?.serviceAccounts) {
      // Seed from embedded GET /tenant billingOpsStatus — do not N×-fetch history on page load.
      // TabBillingOptions loads full { current, history } when that tab opens.
      const statusMap: { [key: string]: TenantAccountBillingStatusResponse } = {}
      for (const account of tenant.serviceAccounts) {
        if (account.billingOpsStatus != null) {
          statusMap[account.id] = {
            current: {
              status: account.billingOpsStatus.status,
              reasons: account.billingOpsStatus.reasons,
              effectiveDate: account.billingOpsStatus.effectiveDate ?? '',
            },
            history: [],
          }
        }
      }

      if (!isActive()) {
        return false
      }
      setBillingStatuses(statusMap)
      return true
    }
    return false
  }

  const serviceAccountIdsKey = tenant?.serviceAccounts?.map((account) => account.id).join(',') ?? ''

  useEffect(() => {
    if (tenant?.id !== tenantId || !tenant?.serviceAccounts) {
      setStatusesLoaded(false)
      return
    }
    let active = true
    const isActive = (): boolean => active && tenant?.id === tenantId
    setStatusesLoaded(false)
    Promise.all([fetchBillingStatuses(isActive), fetchAccountStatuses(isActive)]).then(() => {
      if (isActive()) {
        setStatusesLoaded(true)
      }
    })
    return () => {
      active = false
    }
  }, [tenantId, tenant?.id, serviceAccountIdsKey])

  const handleBillingStatusUpdate = (accountId: string, updatedStatus: TenantAccountBillingStatusResponse) => {
    setBillingStatuses((prev) => ({
      ...prev,
      [accountId]: updatedStatus,
    }))
  }

  const handleAccountStatusUpdate = (
    accountId: string,
    updatedStatus: TenantAccountStatusResponse,
    options?: { refetchTenant?: boolean }
  ) => {
    setAccountStatuses((prev) => ({
      ...prev,
      [accountId]: updatedStatus,
    }))
    // Refetch tenant only after a user status edit so DTO fields (isFinalAccount, utilityStanding, SA active) match.
    // Mount loads from Billing Options must not refetch — that flashes Active Accounts KPI via statusesLoaded.
    if (options?.refetchTenant === true && tenantId) {
      dispatch(getTenant(tenantId as string))
    }
  }

  const handleAccountOpenChange = useCallback(
    (accountId: string, open: boolean) => {
      setOpenByAccountId((prev) => ({ ...prev, [accountId]: open }))
      if (open) {
        const tabIndex = lastTabIndexRef.current
        setTabByAccountId((prev) => (prev[accountId] === tabIndex ? prev : { ...prev, [accountId]: tabIndex }))
      }
      // Retire the deep link so open-state seed, highlight and forceOpen stop
      // re-targeting the searched account. Landing scroll is already one-shot.
      if (shouldClearDeepLinkOnAccountOpen(deepLinkAccountId, accountId, open, qpServiceAccountNumber != null)) {
        setSearchParams((prev) => retireTenantDeepLinkSearchParams(prev), { replace: true })
      }
    },
    [deepLinkAccountId, qpServiceAccountNumber, setSearchParams]
  )

  const allExpanded = useMemo(() => {
    const accounts = tenant?.serviceAccounts ?? []
    if (accounts.length === 0) return false
    return accounts.every((account) => openByAccountId[account.id] === true)
  }, [tenant?.serviceAccounts, openByAccountId])

  const toggleAllSections = () => {
    const next = !allExpanded
    setExpandAllOpen(next)
    setExpandAllVersion((v) => v + 1)
    if (tenant?.serviceAccounts) {
      const map: Record<string, boolean> = {}
      tenant.serviceAccounts.forEach((account) => {
        map[account.id] = next
      })
      setOpenByAccountId(map)
      if (next) {
        const tabIndex = lastTabIndexRef.current
        setTabByAccountId((prev) => {
          const nextTabs = { ...prev }
          for (const account of tenant.serviceAccounts) {
            nextTabs[account.id] = tabIndex
          }
          return nextTabs
        })
      }
    }
  }

  const handleAccountTabSelect = useCallback(
    (accountId: string, index: number) => {
      setLastTabIndex(index)
      setTabByAccountId((prev) => ({ ...prev, [accountId]: index }))
    },
    [setLastTabIndex]
  )

  // Publish expanded account panels + tabs for Sage chat context (no network).
  // Require tenant.id === route tenantId so a stale Redux record is not attributed
  // to the tenant we just navigated to.
  useEffect(() => {
    if (!tenantId || tenant?.id !== tenantId || !tenant.serviceAccounts?.length) {
      setTenantViewFocus(null)
      return
    }
    setTenantViewFocus({
      tenantId,
      accounts: tenant.serviceAccounts.map((account, index) => {
        const isDeepLinkTarget =
          qpServiceAccountNumber != null && matchesServiceAccountNumber(account, qpServiceAccountNumber)
        const open =
          openByAccountId[account.id] ??
          (qpServiceAccountNumber ? isDeepLinkTarget : index === 0)
        const tabIndex =
          tabByAccountId[account.id] ??
          (isDeepLinkTarget && qpTab != null ? qpTab : lastTabIndex)
        return {
          accountId: account.id,
          accountNumber: account.kingAccountNumber,
          accountName: account.kingAccountName,
          open,
          tabIndex,
          tabTitle: tabTitleForIndex(tabIndex, hasConductorRead),
        }
      }),
    })
  }, [
    tenantId,
    tenant?.id,
    tenant?.serviceAccounts,
    openByAccountId,
    tabByAccountId,
    qpServiceAccountNumber,
    qpTab,
    lastTabIndex,
    hasConductorRead,
  ])

  useEffect(() => {
    if (!tenantId || tenant?.id !== tenantId) {
      setNotesViewContext(null)
      return
    }
    setNotesViewContext({
      sourceEntity: `tenant|${tenantId}`,
      label: tenant.name,
      tenantCuid: tenantId,
    })
    return () => setNotesViewContext(null)
  }, [tenantId, tenant?.id, tenant?.name])

  useEffect(() => {
    return () => setTenantViewFocus(null)
  }, [])

  const kpis = useMemo(
    () => computeTenantKpis(tenant?.serviceAccounts ?? [], billingStatuses, accountStatuses, statusesLoaded),
    [tenant?.serviceAccounts, billingStatuses, accountStatuses, statusesLoaded]
  )

  const accountsMounted = !isInitializing && tenant?.id === tenantId && !(loading && !refreshingTenant)

  useDeepLinkAccountScroll({
    loading: !accountsMounted,
    tenantId,
    kingAccountNumber: deepLinkKingAccountNumber,
    accountRefs,
    stickyOffset: accountStickyOffset,
  })

  if (!loading && error && (tenant == null || tenant.id !== tenantId)) {
    return <div className="p-6 text-sm dark:text-white">Tenant not found</div>
  }

  if ((loading && !refreshingTenant) || isInitializing || tenant?.id !== tenantId) {
    return <div className="p-6 text-sm dark:text-white">Loading...</div>
  }

  return (
    <>
      {refreshingTenant && (
        <div className="px-4 sm:px-6 md:px-8 pt-2 text-xs text-slate-500 dark:text-slate-400">Refreshing tenant…</div>
      )}
      <div className="py-6 flex-1 relative focus:outline-none text-sm dark:text-white">
        <div
          id="header"
          ref={pageHeaderRef}
          className={classnames(
            // Separate pl/pr — px-* would override pr and let the title sit under NotesDrawer FAB.
            // FAB is right-6 + w-12 (+ badge); keep ~1.5rem gap beyond that.
            'w-full mx-auto pt-0.5 pb-2 pl-4 sm:pl-6 md:pl-8 pr-24 sm:pr-28 sticky -top-px bg-slate-100 dark:bg-slate-900 z-30'
          )}
        >
          <InlineEditInput
            name="name"
            label="Tenant name"
            hideLabel
            required
            initialValue={tenant?.name}
            handleSubmit={async (editedValue) => {
              await updateTenantApi(tenantId as string, { name: editedValue })
              await dispatch(getTenant(tenantId as string))
            }}
            className="max-w-full text-2xl font-medium dark:text-white"
          />
          <div className="mt-1 space-y-1 text-xs text-slate-600 dark:text-slate-300">
            <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                <span className="shrink-0 text-slate-500 dark:text-slate-400">Property:</span>
                <Link
                  to={`/tenant/tenants?propertyId=${tenant?.property.id}`}
                  className={classnames('min-w-0 max-w-[min(100%,24rem)] truncate', HEADER_META_LINK)}
                  title={
                    tenant?.property.location
                      ? `${tenant.property.name} — ${tenant.property.location}`
                      : tenant?.property.name
                  }
                >
                  {tenant?.property.name}
                  {tenant?.property.location ? ` — ${tenant.property.location}` : ''}
                </Link>
              </span>

              {tenant?.property.utilityShortName ? (
                <>
                  <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                    ·
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="shrink-0 text-slate-500 dark:text-slate-400">Utility:</span>
                    {tenant.property.utilityLoginUrl ? (
                      <a
                        href={tenant.property.utilityLoginUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classnames('inline-flex items-center gap-0.5', HEADER_META_LINK)}
                        title="Utility login"
                      >
                        {tenant.property.utilityShortName}
                        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {tenant.property.utilityShortName}
                      </span>
                    )}
                  </span>
                </>
              ) : null}

              {tenant?.property.programs != null && tenant.property.programs.length > 0 ? (
                <>
                  <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                    ·
                  </span>
                  <HeaderPrograms programs={tenant.property.programs} />
                </>
              ) : null}

              <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                ·
              </span>
              <span className="inline-flex min-w-0 max-w-full items-center gap-1">
                <span className="shrink-0 text-slate-500 dark:text-slate-400">SF Account:</span>
                {hasSalesforceOpsRWPermission() === true ? (
                  <InlineEditInput
                    name="salesforceAccountId"
                    label="Salesforce Account ID"
                    hideLabel
                    required
                    fitContent
                    compact
                    initialValue={tenant?.salesforceAccountId === 'N/A' ? '' : tenant?.salesforceAccountId}
                    displayValue={tenant?.salesforceAccountName ?? undefined}
                    handleSubmit={async (editedValue) => {
                      await updateTenantApi(tenantId as string, { salesforceAccountId: editedValue }).then(() => {
                        dispatch(getTenant(tenantId as string))
                      })
                    }}
                    className="max-w-[16rem] truncate font-medium"
                    linkClassName={classnames(HEADER_META_LINK, 'max-w-[16rem] truncate')}
                    linkTemplate="https://kingenergy.lightning.force.com/lightning/r/Account/@@value@@/view"
                    validationSchema={Yup.string().matches(
                      /^(?:[a-zA-Z0-9]{15}|[a-zA-Z0-9]{18})$/,
                      'Must be 15 or 18 characters'
                    )}
                  />
                ) : tenant?.salesforceAccountId != null && tenant?.salesforceAccountId !== 'N/A' ? (
                  <Link
                    target="_blank"
                    className={classnames('max-w-[16rem] truncate', HEADER_META_LINK)}
                    to={`https://kingenergy.lightning.force.com/lightning/r/Account/${tenant.salesforceAccountId}/view`}
                    title={tenant.salesforceAccountName || tenant.salesforceAccountId}
                  >
                    {tenant.salesforceAccountName || tenant.salesforceAccountId}
                  </Link>
                ) : (
                  <span className="font-medium text-slate-500 dark:text-slate-400">Not set</span>
                )}
              </span>

              {registrationId != null ? (
                <>
                  <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                    ·
                  </span>
                  <Link
                    to={`/tenant/onboarding/${registrationId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classnames('inline-flex items-center gap-0.5', HEADER_META_LINK)}
                  >
                    Onboarding
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </Link>
                </>
              ) : null}
            </div>
            <PanelContact contact={tenant?.contacts?.[0]} />
          </div>
        </div>
        <div className="mx-auto p-4 sm:px-6 md:px-8">
          {(tenant?.serviceAccounts?.length ?? 0) > 0 && <TenantKpiBar kpis={kpis} />}

          <TenantAccountsToolbar
            accountCount={tenant?.serviceAccounts?.length ?? 0}
            allExpanded={allExpanded}
            onToggleAll={toggleAllSections}
            autoScroll={autoScroll}
            onAutoScrollChange={setAutoScroll}
            stickyOffset={pageHeaderHeight}
            toolbarRef={accountsToolbarRef}
          />

          {tenant?.serviceAccounts?.length === 0 && <div className="dark:text-white">No tenant accounts found.</div>}

          <div data-tenant-accounts className="bg-slate-100 dark:bg-slate-900">
          {tenant?.serviceAccounts?.map((serviceAccount, index) => {
            const {
              billType: kingBillType,
              includeChart,
              includeSummary,
              energyDiscount,
              demandDiscount,
              billingMode,
            } = serviceAccount.billingConfiguration.tenantAccountConfiguration ?? {}

            const { billType: utilityBillType, ccaProgram, demandCreditProgram } =
              serviceAccount.billingConfiguration.utilityAccountConfiguration ?? {}

            const isDeepLinkTarget =
              qpServiceAccountNumber != null && matchesServiceAccountNumber(serviceAccount, qpServiceAccountNumber)
            const isOpen =
              openByAccountId[serviceAccount.id] ??
              (qpServiceAccountNumber
                ? isDeepLinkTarget
                : index === 0)
            const tabSelectedKey =
              tabByAccountId[serviceAccount.id] ??
              (isDeepLinkTarget && qpTab != null ? qpTab : lastTabIndex)
            const tabBadges = getInitialLoadTabBadges(serviceAccount)

            return (
              <div ref={accountRefs.current[serviceAccount.kingAccountNumber]} key={serviceAccount.kingAccountNumber}>
                <TenantAccountPanel
                  account={serviceAccount}
                  billingStatuses={billingStatuses}
                  accountStatuses={accountStatuses}
                  billingOptions={billingOptions}
                  open={isOpen}
                  forceOpen={isDeepLinkTarget}
                  expandAllOpen={expandAllOpen}
                  expandAllVersion={expandAllVersion}
                  onOpenChange={(open) => handleAccountOpenChange(serviceAccount.id, open)}
                  isHighlighted={isHighlighting && isDeepLinkTarget}
                  stickyOffset={accountStickyOffset}
                  stickyZIndex={20}
                  autoScrollOnExpand={autoScroll}
                  pinnedChipIds={pinnedChipIds}
                  onToggleHeaderChip={(id) => setPinnedChipIds(toggleHeaderChip(pinnedChipIds, id))}
                  onResetHeaderChips={() => setPinnedChipIds([...DEFAULT_HEADER_CHIP_IDS])}
                  headerAccessory={
                    hasConductorRead && isOpen && conductorTab != null ? (
                      <ConductorAccountChip
                        subjectCuid={serviceAccount.id}
                        onSelectTab={() => handleAccountTabSelect(serviceAccount.id, conductorTab)}
                        onTabBadge={(badge) => {
                          setConductorTabBadges((prev) =>
                            prev[serviceAccount.id] === badge ? prev : { ...prev, [serviceAccount.id]: badge }
                          )
                        }}
                      />
                    ) : null
                  }
                >
                  <TabPanel
                    selectedKey={Number.isFinite(tabSelectedKey) ? tabSelectedKey : 0}
                    onSelect={(index) => handleAccountTabSelect(serviceAccount.id, index)}
                    updateUrl={false}
                    autoScroll={autoScroll}
                    scrollOffset={accountStickyOffset}
                  >
                    <TabGeneral
                      title="General"
                      data={serviceAccount}
                    />
                    <TabMailingPreference title="Mailing preference" data={serviceAccount} />
                    <TabServiceAgreements
                      title="Service agreements"
                      badge={tabBadges.serviceAgreements}
                      data={serviceAccount.serviceAgreements.map((sa) => ({
                        ...sa,
                        tenantAccountNumber: serviceAccount.kingAccountNumber,
                      }))}
                      hasSalesforceOpsROPermission={hasSalesforceOpsROPermission}
                      hasSalesforceOpsRWPermission={hasSalesforceOpsRWPermission}
                      hasServiceAgreementSuperuserPermission={hasServiceAgreementSuperuserPermission}
                      utilityName={serviceAccount.utilityName}
                      tenantAccountId={serviceAccount.id}
                      ccaProgram={ccaProgram}
                    />
                    <TabAccountManagers
                      title="Account managers"
                      badge={tabBadges.accountManagers}
                      tenantAccountCuid={serviceAccount.id}
                      data={serviceAccount.accountManagers}
                    />
                    <TabPayments title="Payments" serviceAccount={serviceAccount} />
                    <TabInvoices
                      title="Invoices"
                      badge={tabBadges.invoices}
                      data={serviceAccount.invoices}
                      tenantAccountId={serviceAccount.id}
                      utilityName={serviceAccount.utilityName}
                      invoicesEnriched={serviceAccount.invoicesEnriched}
                    />
                    <TabBillingEvents title="Billing events" tenantAccountId={serviceAccount.id} />
                    <TabBillingOptions
                      title="Billing options"
                      kingFormInitialValues={{
                        billType: kingBillType,
                        billingMode,
                        includeChart,
                        includeSummary,
                        energyDiscount,
                        demandDiscount,
                      }}
                      utilityFormInitialValues={{
                        billType: utilityBillType,
                        ccaProgram,
                        demandCreditProgram,
                      }}
                      utilityBillingOptions={billingOptions?.utilityBillTypes ?? [{ STANDARD: 'Standard' }]}
                      kingBillingOptions={billingOptions?.kingBillTypes ?? [{ STANDARD: 'Standard' }]}
                      enrollmentOptions={billingOptions?.enrollmentStatuses ?? [{ ENROLLED: 'Enrolled' }, { NOT_ENROLLED: 'Not enrolled' }]}
                      billingModeOptions={billingOptions?.billingMode ?? [{ STANDARD: 'Standard' }]}
                      tenantAccountId={serviceAccount.id}
                      billingStatus={billingStatuses[serviceAccount.id]}
                      accountStatus={accountStatuses[serviceAccount.id]}
                      isFinalAccount={serviceAccount.isFinalAccount === true}
                      onBillingStatusUpdate={handleBillingStatusUpdate}
                      onAccountStatusUpdate={handleAccountStatusUpdate}
                      onBillingConfigUpdate={() => {
                        dispatch(getTenant(tenantId as string))
                      }}
                    />
                    <TabIncentivePlans
                      title="Incentives"
                      badge={tabBadges.incentives}
                      data={serviceAccount.incentivePlans}
                      tenantAccountId={serviceAccount.id}
                      incentivePlanOptions={incentivePlanOptions}
                      lastKingInvoice={
                        serviceAccount.currentInvoice
                          ? {
                              billingStartDate: serviceAccount.currentInvoice.billingStartDate,
                              billingEndDate: serviceAccount.currentInvoice.billingEndDate,
                              dueDate: serviceAccount.currentInvoice.dueDate,
                            }
                          : undefined
                      }
                      utilityName={serviceAccount.utilityName}
                      onRefresh={() => dispatch(getTenant(tenantId as string)).then(() => undefined)}
                    />
                    <TabDocuments
                      title="Other Documents"
                      description="Not available elsewhere (TESA, Demand Letters, utility bills not yet on invoices, etc.)"
                      tenantAccountId={serviceAccount.id}
                    />
                    {/* Order must match tenantAccountTabTitles(): Conductor last so 0–9 stay stable. */}
                    {hasConductorRead ? (
                      <TabConductor
                        title="Conductor"
                        badge={conductorTabBadges[serviceAccount.id]}
                        description="Workflows and gates for this account. Approvals and Inbox stay on Conductor."
                        tenantAccountCuid={serviceAccount.id}
                      />
                    ) : null}
                  </TabPanel>
                </TenantAccountPanel>
              </div>
            )
          })}
          </div>
        </div>
      </div>
    </>
  )
}

export default Tenant
